import { ListStyleType } from '@udecode/plate-indent-list';
import { repeat } from 'lodash-es';
function isTextNode(node) {
    return 'text' in node;
}
function haveFormatting(node) {
    return Boolean(node.bold || node.italic || node.underline || node.strikethrough || node.code);
}
// checks against if any of the formatting matches
function haveSimilarFormatting(a, b) {
    const similarMarkdownFormatting = Boolean(a.bold && b.bold) ||
        Boolean(a.italic && b.italic) ||
        Boolean(a.strikethrough && b.strikethrough) ||
        Boolean(a.code && b.code) ||
        Boolean(a.underline && b.underline);
    // for underline as we create custom tag we consider two consecutive text nodes as similar formatting
    // only if both have same underline value
    return similarMarkdownFormatting && a.underline === b.underline;
}
function addListPrefix(node, content) {
    if (!node.listStyleType) {
        return content;
    }
    let prefix = '';
    if (node.listStyleType === ListStyleType.Disc) {
        prefix = '-';
    }
    if (node.listStyleType === ListStyleType.Decimal) {
        prefix = '1.';
    }
    return `${repeat('  ', typeof node.indent === 'number' ? node.indent - 1 : 0)}${prefix} ${content}`;
}
function isElement(node) {
    return 'type' in node;
}
function serializeList(nodes, unordered) {
    let markdown = '';
    nodes.forEach((node, index) => {
        if (isElement(node) && node.type === 'li') {
            let bullet = unordered ? '-' : `${index + 1}.`;
            const content = serializeToMd(node.children || []);
            markdown += `${bullet} ${content}\n`;
        }
    });
    return markdown + '\n';
}
function serializeNode(node) {
    switch (node.type) {
        case 'h1':
            return '# ' + serializeToMd(node.children || []) + '\n\n';
        case 'h2':
            return '## ' + serializeToMd(node.children || []) + '\n\n';
        case 'h3':
            return '### ' + serializeToMd(node.children || []) + '\n\n';
        case 'p':
            return serializeToMd(node.children || []) + '\n\n';
        case 'blockquote':
            return '> ' + serializeToMd(node.children || []) + '\n\n';
        case 'ul':
            return serializeList(node.children || [], true);
        case 'ol':
            return serializeList(node.children || [], false);
        case 'li':
            return serializeToMd(node.children || []) + '\n';
        case 'lic':
            return serializeToMd(node.children || []);
        case 'img':
            return `![${node.alt || ''}](${node.url || ''})\n\n`;
        case 'table':
            return serializeTable(node.children || []) + '\n\n';
        case 'tr':
            return serializeTableRow(node.children || []) + '\n';
        case 'th':
            return serializeTableCell(node.children || []);
        case 'td':
            return serializeTableCell(node.children || []);
        case 'a':
            return `[${serializeToMd(node.children || []) || ''}](${node.url || ''})\n\n`;
        case 'code_block':
            return (`\n\n` +
                addListPrefix(node, `\`\`\`${node.lang ? node.lang : ''}\n` + serializeToMd(node.children || []) + `\n\`\`\``) +
                `\n\n`);
        case 'code_line':
            return '\n' + serializeToMd(node.children || []);
        case 'mention':
            // Handle mention nodes - return the mention value (which includes @)
            return node.value || '';
        case 'hr':
            return '---\n';
        default:
            return serializeToMd(node.children || []);
    }
}
const CodeToken = '`';
const BoldToken = '**';
const BoldAlternateToken = '__';
const ItalicToken = '*';
const StrikethroughToken = '~~';
const formatTokens = [
    { key: 'underline', start: '<u>', end: '</u>', alternate: '<u>' },
    { key: 'code', start: CodeToken, end: CodeToken, alternate: CodeToken },
    { key: 'bold', start: BoldToken, end: BoldToken, alternate: BoldAlternateToken },
    { key: 'italic', start: ItalicToken, end: ItalicToken, alternate: ItalicToken },
    { key: 'strikethrough', start: StrikethroughToken, end: StrikethroughToken, alternate: StrikethroughToken }
];
function unPadSpaces(text) {
    const matches = text.match(/^(\s*)(\S.*?)(\s*)$/s);
    return {
        prefix: matches?.[1] || '',
        content: matches?.[2] || '',
        suffix: matches?.[3] || ''
    };
}
function formatTextNode(node, markdown) {
    if (!node.text)
        return '';
    const { prefix, content, suffix } = unPadSpaces(node.text);
    let text = content;
    for (let i = formatTokens.length - 1; i >= 0; i--) {
        const token = formatTokens[i];
        // in case the previous node is closed by italic we nee
        if (token.key === 'bold' && node[token.key] && markdown.endsWith(ItalicToken)) {
            text = token.alternate + text + token.alternate;
        }
        else if (node[token.key]) {
            text = token.start + text + token.end;
        }
    }
    return prefix + text + suffix;
}
function formatConsecutiveTextNodes(nodes, markdown) {
    let useAlternateBold = markdown.endsWith(ItalicToken);
    let closingTokens = {};
    let text = '';
    let firstNodePrefix = '';
    let lastNodeSuffix = '';
    nodes.forEach((node, index) => {
        const newEndTokens = {};
        let textPrefix = '';
        const currentClosingTokens = Object.keys(closingTokens).reverse();
        // close existing formatting tokens
        for (const token of currentClosingTokens) {
            // if same formatting is not used as current node, we need to close the tag
            if (!node[closingTokens[token]]) {
                textPrefix = textPrefix + token;
                delete closingTokens[token];
            }
        }
        // add new formatting tokens
        for (const token of formatTokens) {
            // if a new formatting is found, we need to add the opening token
            if (node[token.key] && !(closingTokens[token.end] || closingTokens[token.alternate])) {
                // we need to avoid adding ambiguous sequence like ***, in which its hard to find whether italic is first or bold is first
                if (token.key === 'bold' && useAlternateBold) {
                    newEndTokens[token.alternate] = token.key;
                    textPrefix = textPrefix + token.alternate;
                }
                else {
                    newEndTokens[token.end] = token.key;
                    // opening token should go on end
                    textPrefix = textPrefix + token.start;
                }
                useAlternateBold = textPrefix.endsWith(ItalicToken);
            }
        }
        let currentText = node.text;
        if (index === 0) {
            const { prefix, content, suffix } = unPadSpaces(currentText);
            firstNodePrefix = prefix;
            currentText = content + suffix;
        }
        if (index === nodes.length - 1) {
            const { prefix, content, suffix } = unPadSpaces(currentText);
            lastNodeSuffix = suffix;
            currentText = prefix + content;
        }
        text = text + textPrefix + currentText;
        useAlternateBold = text.endsWith(ItalicToken);
        // merge new closing tokens with existing closing tokens
        closingTokens = { ...closingTokens, ...newEndTokens };
    });
    // append remaining closing tokens in reverse order
    return firstNodePrefix + text + Object.keys(closingTokens).reverse().join('') + lastNodeSuffix;
}
function serializeTable(rows) {
    let markdown = '';
    let headerRow = '';
    let separatorRow = '';
    let bodyRows = '';
    rows.forEach((row, index) => {
        const rowContent = serializeTableRow(row.children || []);
        if (index === 0) {
            headerRow = rowContent.trim();
            const cellCount = row.children?.length || 0;
            separatorRow = `| ${Array(cellCount).fill('---').join(' | ')} |`;
        }
        else {
            bodyRows += rowContent;
        }
    });
    markdown = `${headerRow}\n${separatorRow}\n${bodyRows}`;
    return markdown;
}
function serializeTableRow(cells) {
    const rowContent = cells
        .map((cell) => {
        return serializeTableCell(cell.children || []);
    })
        .join(' | ');
    return `| ${rowContent} |\n`;
}
function serializeTableCell(nodes) {
    const content = serializeToMd(nodes).trim();
    return content;
}
export function serializeToMd(nodes) {
    let markdown = '';
    let i = 0;
    while (i < nodes.length) {
        const node = nodes[i];
        if (isTextNode(node) && haveFormatting(node)) {
            // if we get two or more consecutive text nodes with same formatting, we can combine them
            // as we break the text node into multiple node to handle partial highlights
            let consecutiveNodes = [node];
            while (i + 1 < nodes.length && isTextNode(nodes[i + 1]) && haveSimilarFormatting(node, nodes[i + 1])) {
                consecutiveNodes.push(nodes[i + 1]);
                i++;
            }
            if (consecutiveNodes.length === 1) {
                markdown += formatTextNode(consecutiveNodes[0], markdown);
            }
            else {
                markdown += formatConsecutiveTextNodes(consecutiveNodes, markdown);
            }
        }
        else if (isTextNode(node)) {
            markdown += node.text;
        }
        else {
            markdown += serializeNode(node);
        }
        i++;
    }
    return markdown.trim();
}
